/** The three ceilings a plan meters, exactly as the API names them. */
export type LimitedResource = 'branches' | 'users' | 'devices';

/**
 * The `code` the API stamps on a 403 thrown by `assertWithinPlanLimits`.
 * `SubscriptionsService` exports the same constant; the two are asserted against each other by
 * nothing, so the string is written once here and imported everywhere the dashboard needs it.
 */
export const PLAN_LIMIT_REACHED = 'PLAN_LIMIT_REACHED';

/** What the API says when a create is refused for being past the plan. */
export interface PlanLimitRefusal {
  resource: LimitedResource;
  limit: number;
  used: number;
  /** The plan's display name, or null when the tenant has no live subscription at all. */
  planName: string | null;
  planCode: string | null;
}

const RESOURCES: LimitedResource[] = ['branches', 'users', 'devices'];

/**
 * Recognise the one refusal this screen can do something about.
 *
 * `assertWithinPlanLimits` is real and enforced — a venue on STARTER genuinely cannot open a
 * second branch — and until now the whole feature ended at that 403: the dialog put the
 * server's English sentence in a red toast and the operator was left at a wall with no door.
 *
 * The server now answers with a body rather than a sentence, so this reads the four fields that
 * matter and lets the caller render a translated, actionable message with the real numbers in
 * it. Deliberately strict: every field is type-checked, and anything that does not match
 * exactly returns null so the caller falls back to the ordinary error path. A near-miss shape
 * silently rendering "undefined of undefined" would be worse than the toast this replaces.
 */
export function planLimitRefusal(error: unknown): PlanLimitRefusal | null {
  const body = (error as { response?: { data?: unknown } })?.response?.data;
  if (!body || typeof body !== 'object') return null;

  const data = body as Record<string, unknown>;
  if (data.code !== PLAN_LIMIT_REACHED) return null;
  if (!RESOURCES.includes(data.resource as LimitedResource)) return null;
  if (typeof data.limit !== 'number' || typeof data.used !== 'number') return null;

  return {
    resource: data.resource as LimitedResource,
    limit: data.limit,
    used: data.used,
    planName: typeof data.planName === 'string' ? data.planName : null,
    planCode: typeof data.planCode === 'string' ? data.planCode : null,
  };
}
